import { useEffect, useState } from 'react';
import api from '../../services/api';
import Button from '../../components/Button';
import Select from '../../components/Select';
import Input from '../../components/Input';
import Table, { Column } from '../../components/Table';
import StatusBadge from '../../components/StatusBadge';

interface Template {
  id: string;
  code: string;
  name: string;
}
interface UnassignedEmployee {
  id: string;
  code: string;
  full_name: string;
}
interface Assignment {
  id: string;
  status: string;
  published: boolean;
  employee_id: string;
  code: string;
  full_name: string;
  template_name: string;
}

const TABS = [
  { key: 'unassigned', label: 'Unassigned' },
  { key: 'assigned', label: 'Assigned' },
  { key: 'reassign', label: 'Reassign' },
] as const;

const MONTHS = Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: String(i + 1).padStart(2, '0') }));

export default function AssignAppraisal() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [month, setMonth] = useState(String(new Date().getMonth() + 1));
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [tab, setTab] = useState<(typeof TABS)[number]['key']>('unassigned');

  const [unassigned, setUnassigned] = useState<UnassignedEmployee[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [assigned, setAssigned] = useState<Assignment[]>([]);
  const [reassignTemplateId, setReassignTemplateId] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    api.get('/pas/masters/templates').then((res) => {
      const list = res.data.templates || [];
      setTemplates(list);
      if (list.length && !templateId) setTemplateId(list[0].id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    if (!templateId || !month || !year) return;
    setLoading(true);
    try {
      if (tab === 'unassigned') {
        const res = await api.get('/pas/assignments', { params: { templateId, month, year, tab: 'unassigned' } });
        setUnassigned(res.data.employees || []);
        setSelected([]);
      } else {
        const res = await api.get('/pas/assignments', { params: { templateId, month, year, tab: 'assigned' } });
        setAssigned(res.data.assignments || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, month, year, tab]);

  const toggleSelected = (id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const assign = async () => {
    if (!selected.length) return;
    setLoading(true);
    try {
      const res = await api.post('/pas/assignments', { templateId, month, year, employeeIds: selected });
      setMessage(`Assigned ${res.data.created} employee(s)`);
      load();
    } finally {
      setLoading(false);
    }
  };

  const reassign = async (assignmentId: string) => {
    const newTemplateId = reassignTemplateId[assignmentId];
    if (!newTemplateId) return;
    await api.put(`/pas/assignments/${assignmentId}/reassign`, { templateId: newTemplateId });
    setMessage('Reassigned');
    load();
  };

  const unassignedColumns: Column<UnassignedEmployee>[] = [
    {
      key: 'select',
      header: '',
      render: (r) => <input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggleSelected(r.id)} />,
    },
    { key: 'code', header: 'Code' },
    { key: 'full_name', header: 'Name' },
  ];

  const retryChain = async (assignmentId: string) => {
    await api.post(`/pas/assignments/${assignmentId}/retry-chain`);
    setMessage('Approval chain started');
    load();
  };

  const assignedColumns: Column<Assignment>[] = [
    { key: 'code', header: 'Code' },
    { key: 'full_name', header: 'Name' },
    { key: 'template_name', header: 'Template' },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    { key: 'published', header: 'Published', render: (r) => (r.published ? 'Yes' : 'No') },
    {
      key: 'actions',
      header: '',
      render: (r) =>
        r.status === 'ASSIGNED' ? (
          <Button variant="ghost" onClick={() => retryChain(r.id)}>Start Approval Chain</Button>
        ) : null,
    },
  ];

  const reassignColumns: Column<Assignment>[] = [
    { key: 'code', header: 'Code' },
    { key: 'full_name', header: 'Name' },
    { key: 'template_name', header: 'Current Template' },
    {
      key: 'newTemplate',
      header: 'New Template',
      render: (r) => (
        <div className="flex gap-2 items-center">
          <select
            className="border rounded px-2 py-1 text-sm"
            value={reassignTemplateId[r.id] || ''}
            onChange={(e) => setReassignTemplateId({ ...reassignTemplateId, [r.id]: e.target.value })}
          >
            <option value="">Select template...</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          <Button variant="secondary" onClick={() => reassign(r.id)} disabled={!reassignTemplateId[r.id]}>
            Reassign
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Assign Appraisal</h1>
      {message && <div className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded px-4 py-2">{message}</div>}

      <div className="bg-white rounded-lg shadow p-4 mb-4 grid grid-cols-3 gap-4">
        <Select label="Template" value={templateId} onChange={(e) => setTemplateId(e.target.value)} options={templates.map((t) => ({ value: t.id, label: t.name }))} />
        <Select label="Month" value={month} onChange={(e) => setMonth(e.target.value)} options={MONTHS} />
        <Input label="Year" type="number" value={year} onChange={(e) => setYear(e.target.value)} />
      </div>

      <div className="flex gap-2 mb-4 border-b">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium ${tab === t.key ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'unassigned' && (
        <>
          <div className="mb-3">
            <Button onClick={assign} disabled={!selected.length}>Assign Selected ({selected.length})</Button>
          </div>
          <Table columns={unassignedColumns} rows={unassigned} loading={loading} emptyMessage="All active employees are assigned for this template/period" />
        </>
      )}

      {tab === 'assigned' && (
        <Table columns={assignedColumns} rows={assigned} loading={loading} emptyMessage="No assignments yet" />
      )}

      {tab === 'reassign' && (
        <Table
          columns={reassignColumns}
          rows={assigned.filter((a) => a.status === 'ASSIGNED')}
          loading={loading}
          emptyMessage="No assignments are eligible for reassignment (only unsubmitted assignments can be reassigned)"
        />
      )}
    </div>
  );
}
